/**
 * 智能视觉扫描卡片组件
 * 卡片2：展示 AI 识别的视觉证据（病灶、位置、特征）
 * 职责：只展示"看到了什么" (Evidence)
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input"; // Ensure this input component exists
import { Button } from "@/components/ui/button";
import {
  Scan,
  Eye,
  Fish,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Pencil,
  Check,
  X,
  Wind,
  Activity,
  ShieldCheck,
} from "lucide-react";
import { useFeedbackState } from "@/hooks/useFeedbackState";
import { cn } from "@/lib/utils";

export interface VisualEvidence {
  body_part: string;
  symptom: string;
  bbox_x?: number;
  bbox_y?: number;
}

export interface VisualScanCardProps {
  assessmentId?: string | number;
  fishSpecies?: string;
  confidence?: number;
  visualEvidence: VisualEvidence[];
  visualSummary?: string;
  isLoading?: boolean;
  onHoverFeature?: (featureIdx: number | null) => void;
  initialFeedbackStatus?: {
    user_feedback: "positive" | "negative" | null;
    can_submit_feedback: boolean;
  };
}

/** 部位图标映射 */
const getBodyPartIcon = (bodyPart: string): React.ReactNode => {
  const lowerPart = bodyPart.toLowerCase();

  if (lowerPart.includes("头") || lowerPart.includes("head"))
    return <div className="text-xl">🔹</div>;
  if (lowerPart.includes("眼") || lowerPart.includes("eye"))
    return <Eye className="w-5 h-5 text-blue-500" />;
  if (lowerPart.includes("体表") || lowerPart.includes("body"))
    return <Fish className="w-5 h-5 text-indigo-500" />;
  if (lowerPart.includes("背鳍") || lowerPart.includes("dorsal"))
    return <Wind className="w-5 h-5 text-sky-500 transform -rotate-45" />;
  if (
    lowerPart.includes("尾") ||
    lowerPart.includes("tail") ||
    lowerPart.includes("caudal")
  )
    return <Wind className="w-5 h-5 text-blue-400" />;
  if (lowerPart.includes("鳃") || lowerPart.includes("gill"))
    return <Activity className="w-5 h-5 text-rose-400" />;
  if (lowerPart.includes("腹") || lowerPart.includes("abdomen"))
    return <div className="text-xl">⚪</div>;
  if (lowerPart.includes("鳍") || lowerPart.includes("fin"))
    return <Wind className="w-5 h-5 text-sky-500" />;
  return <div className="text-xl">📍</div>;
};

/** 置信度图标颜色 */
const getConfidenceIconColor = (confidence: number) => {
  if (confidence >= 85) {
    return "green" as const;
  }
  if (confidence >= 60) {
    return "yellow" as const;
  }
  return "red" as const;
};

/** 置信度图标组件 */
const ConfidenceIcon = ({ color }: { color: "green" | "yellow" | "red" }) => {
  const iconClass = "h-3.5 w-3.5";
  switch (color) {
    case "green":
      return (
        <CheckCircle
          className={`${iconClass} text-green-600 dark:text-green-500`}
        />
      );
    case "yellow":
      return (
        <AlertCircle
          className={`${iconClass} text-amber-600 dark:text-amber-500`}
        />
      );
    case "red":
      return (
        <XCircle className={`${iconClass} text-red-600 dark:text-red-500`} />
      );
  }
};

export function VisualScanCard({
  assessmentId,
  fishSpecies,
  confidence,
  visualEvidence,
  visualSummary,
  isLoading = false,
  onHoverFeature,
  initialFeedbackStatus,
}: VisualScanCardProps) {
  const { t } = useTranslation("assessments");
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null);

  // Edit Species State
  const [isEditingSpecies, setIsEditingSpecies] = useState(false);
  const [displaySpecies, setDisplaySpecies] = useState<string>("");
  const [tempSpeciesInput, setTempSpeciesInput] = useState("");

  useEffect(() => {
    setDisplaySpecies(
      fishSpecies || t("pipeline.visualScan.unknownSpecies", "未知鱼种"),
    );
  }, [fishSpecies, t]);

  const handleStartEdit = () => {
    setTempSpeciesInput(displaySpecies);
    setIsEditingSpecies(true);
  };

  const handleSaveEdit = () => {
    if (tempSpeciesInput.trim()) {
      setDisplaySpecies(tempSpeciesInput.trim());
    }
    setIsEditingSpecies(false);
  };

  const handleCancelEdit = () => {
    setIsEditingSpecies(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  // 使用 useFeedbackState hook 管理反馈状态
  const { feedbackState, submitFeedback } = useFeedbackState({
    assessmentId: assessmentId || "",
    initialState: initialFeedbackStatus
      ? {
          status: initialFeedbackStatus.user_feedback ? "success" : "idle",
          userFeedback: initialFeedbackStatus.user_feedback,
          canSubmit: initialFeedbackStatus.can_submit_feedback,
        }
      : undefined,
  });

  const handleHover = (idx: number | null) => {
    setHoveredIdx(idx);
    onHoverFeature?.(idx);
  };

  const handleFeedback = async (feedback: "positive" | "negative") => {
    if (!assessmentId) return;
    await submitFeedback(feedback);
  };

  if (isLoading) {
    return (
      <Card className="w-full md:w-[320px] lg:w-[360px] aspect-square md:aspect-auto md:h-[320px] lg:h-[360px] shrink-0 overflow-hidden border border-white dark:border-border bg-card p-0 gap-0 shadow-[0_0_12px_rgba(0,0,0,0.08)]">
        <CardContent className="p-4 sm:p-6 space-y-4 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-4 w-40" />
          <div className="space-y-3 pt-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const confidenceIconColor =
    confidence != null ? getConfidenceIconColor(confidence) : null;

  // Check if healthy (no detected symptoms)
  const isHealthy = visualEvidence.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="w-full md:w-auto"
    >
      <Card className="w-full md:w-[320px] lg:w-[360px] md:min-h-[320px] lg:min-h-[360px] md:max-h-[640px] lg:max-h-[720px] shrink-0 overflow-hidden border border-white dark:border-border bg-card p-0 gap-0 shadow-[0_0_12px_rgba(0,0,0,0.08)] flex flex-col">
        {/* Header - Optimized for Information Hierarchy */}
        <div className="flex flex-col gap-2 p-4 sm:p-6 pb-3 border-b border-border/40 shrink-0 bg-muted/10">
          <div className="flex items-center gap-2 mb-1">
            <Scan className="h-4 w-4 text-blue-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t("pipeline.visualScan.title", "AI 视觉扫描")}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isEditingSpecies ? (
              <div className="flex items-center gap-1 w-full animate-in fade-in zoom-in-95 duration-200">
                <Input
                  value={tempSpeciesInput}
                  onChange={(e) => setTempSpeciesInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="h-8 text-lg font-bold px-2 py-1"
                  autoFocus
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-green-600"
                  onClick={handleSaveEdit}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-muted-foreground"
                  onClick={handleCancelEdit}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 w-full group">
                <h2 className="text-xl font-bold leading-tight break-words text-foreground">
                  {displaySpecies}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleStartEdit}
                  className="h-7 px-2 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted font-normal"
                  aria-label={t("pipeline.visualScan.editSpecies", "修改")}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {displaySpecies ===
                    t("pipeline.visualScan.unknownSpecies", "未知鱼种") && (
                    <span className="ml-1.5 text-xs">
                      {t("pipeline.visualScan.addSpecies", "补充鱼种")}
                    </span>
                  )}
                </Button>
              </div>
            )}

            {confidence != null && confidenceIconColor && !isEditingSpecies && (
              <Badge
                variant="outline"
                className={cn(
                  "text-xs px-2 py-0.5 flex items-center gap-1 border-opacity-50",
                  confidenceIconColor === "green"
                    ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
                    : confidenceIconColor === "yellow"
                      ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800"
                      : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
                )}
              >
                <ConfidenceIcon color={confidenceIconColor} />
                <span className="font-medium">{Math.round(confidence)}%</span>
              </Badge>
            )}
          </div>
        </div>

        {/* Content area - grows to fill space */}
        <div className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="flex-1">
            <CardContent className="p-4 sm:p-6 pt-4 space-y-6">
              {/* 核心扫描结果区 */}
              <div>
                {isHealthy ? (
                  // Healthy State - Positive Reinforcement
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-8 h-8 text-emerald-500" />
                      <h3 className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {t(
                          "pipeline.visualScan.healthy.title",
                          "未发现异常病灶",
                        )}
                      </h3>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      {t(
                        "pipeline.visualScan.healthy.description",
                        "AI 已完成全身 {{count}} 个部位的扫描",
                        { count: 6 },
                      )}
                    </p>

                    {/* "Proof of Work" Checklist */}
                    <div className="grid gap-2.5">
                      {[
                        {
                          key: "body",
                          icon: <Fish className="w-4 h-4 text-foreground" />,
                          label: t(
                            "pipeline.visualScan.bodyParts.body",
                            "体表",
                          ),
                          status: t(
                            "pipeline.visualScan.status.intact",
                            "完整无伤",
                          ),
                        },
                        {
                          key: "fins",
                          icon: <Wind className="w-4 h-4 text-foreground" />,
                          label: t(
                            "pipeline.visualScan.bodyParts.fins",
                            "鱼鳍",
                          ),
                          status: t(
                            "pipeline.visualScan.status.relaxed",
                            "舒展",
                          ),
                        },
                        {
                          key: "eyes",
                          icon: <Eye className="w-4 h-4 text-foreground" />,
                          label: t(
                            "pipeline.visualScan.bodyParts.eyes",
                            "眼睛",
                          ),
                          status: t("pipeline.visualScan.status.clean", "清晰"),
                        },
                      ].map((item) => (
                        <div
                          key={item.key}
                          className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-50/10 dark:bg-emerald-900/5 border border-emerald-100/20 dark:border-emerald-900/10"
                        >
                          <div className="flex items-center gap-2.5">
                            {item.icon}
                            <span className="text-sm font-medium text-foreground dark:text-gray-200">
                              {item.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-xs text-muted-foreground">
                              {item.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Issues Detected State
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-amber-600 dark:text-amber-500 flex items-center gap-2 mb-3">
                      <AlertCircle className="h-4 w-4" />
                      {t(
                        "pipeline.visualScan.featuresDetected",
                        "检测到视觉特征",
                      )}
                    </h4>

                    <div className="space-y-2">
                      {visualEvidence.map((evidence, idx) => (
                        <motion.div
                          key={idx}
                          className={`relative flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            hoveredIdx === idx
                              ? "bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 scale-[1.01]"
                              : "bg-muted/30 border-border/60 hover:bg-muted"
                          }`}
                          onMouseEnter={() => handleHover(idx)}
                          onMouseLeave={() => handleHover(null)}
                          whileHover={{ x: 2 }}
                        >
                          {/* 部位图标 */}
                          <div className="mt-0.5 text-amber-500/80">
                            {getBodyPartIcon(evidence.body_part)}
                          </div>

                          <div className="flex-1 min-w-0">
                            {/* 部位名称 */}
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-foreground">
                                {evidence.body_part}
                              </span>
                              {hoveredIdx === idx && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1 py-0 border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400"
                                >
                                  {t(
                                    "pipeline.result.viewLocation",
                                    "查看位置",
                                  )}
                                </Badge>
                              )}
                            </div>

                            {/* 症状描述 */}
                            <p className="text-xs text-muted-foreground leading-relaxed font-medium">
                              {evidence.symptom}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 视觉总结 - Note Style */}
              {visualSummary && (
                <div className="p-4 bg-muted/50 rounded-xl border border-border/40 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-purple-500" />
                  <h5 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5 uppercase tracking-wide">
                    <Scan className="w-3 h-3" />
                    {t("pipeline.visualScan.visualSummary", "视觉总结")}
                  </h5>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {visualSummary}
                  </p>
                </div>
              )}
            </CardContent>
          </ScrollArea>

          {/* Feedback Section - Redesigned */}
          {/* Only show if not previously submitted (hidden for history records with existing feedback) */}
          {assessmentId && !initialFeedbackStatus?.user_feedback && (
            <div className="shrink-0 px-4 sm:px-6 pb-4 sm:pb-6 pt-2">
              <div className="pt-4 border-t border-dashed border-border/60">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t("feedback.question", "识别结果准确吗？")}
                  </span>
                  <div className="flex gap-2">
                    {feedbackState.status === "success" ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full border border-border/50"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span className="font-medium">
                          {t("feedback.thanks", "感谢反馈")}
                        </span>
                      </motion.div>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFeedback("positive")}
                          disabled={
                            feedbackState.status === "submitting" ||
                            !feedbackState.canSubmit
                          }
                          className="h-8 px-3 rounded-full hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-900/20 dark:hover:text-green-400 transition-all"
                          title={t("feedback.accurate", "准确")}
                        >
                          <ThumbsUp className="h-3.5 w-3.5 mr-1.5" />
                          <span className="text-xs">
                            {t("feedback.accurate", "准确")}
                          </span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFeedback("negative")}
                          disabled={
                            feedbackState.status === "submitting" ||
                            !feedbackState.canSubmit
                          }
                          className="h-8 px-3 rounded-full hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-all"
                          title={t("feedback.inaccurate", "不准确")}
                        >
                          <ThumbsDown className="h-3.5 w-3.5 mr-1.5" />
                          <span className="text-xs">
                            {t("feedback.inaccurate", "不准确")}
                          </span>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

export default VisualScanCard;
